import { useEffect, useMemo, useState } from 'react';
import Button from '../../../shared/components/Button';
import { Toggle } from '../../../shared/components/Toggle';
import { usePermissions, Permission } from '../../../shared/hooks/usePermissions';
import {
  useMerchantOnboardingFields,
  useSetMerchantOnboardingFields,
} from '../hooks/useMerchantOnboardingFields';
import { buildOnboardingFieldsPayload } from '../payload';
import {
  ALWAYS_REQUIRED_FIELD_KEYS,
  FIELD_DESCRIPTIONS,
  FIELD_LABELS,
  MerchantOnboardingFieldRequirement,
  type MerchantOnboardingFieldKey,
} from '../types';

const { REQUIRED, OPTIONAL } = MerchantOnboardingFieldRequirement;

type Draft = Record<MerchantOnboardingFieldKey, MerchantOnboardingFieldRequirement>;

const formatDate = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

const ALWAYS_REQUIRED_REASON =
  'Always required — merchant creation consumes this field unconditionally (slug, login email, admin name), so it can never be made optional.';

/**
 * Self-onboarding field configuration (PE-2181, backend PE-2180).
 *
 * TCS staff decide which fields the public `/onboard/signup` form insists on. The rows are
 * GLOBAL — not per merchant, not per contract template — and the setting takes effect on the
 * very next submission with no deploy (watchman reads the config fresh per request).
 *
 * ⚠️ NO RESET TO DEFAULT this round. watchman exposes only GET + PUT (upsert) on
 * `/merchant-onboarding-fields` — there is no delete route and the defaults live server-side
 * in DEFAULT_ONBOARDING_FIELD_REQUIREMENTS. Saving a change therefore turns that row into a
 * permanent admin override. Rather than fake a reset by PUTting the default value back (which
 * would leave `isDefault: false` and make the badge lie), Save sends only the keys that
 * actually changed and the badge stays truthful. A `DELETE
 * /merchant-onboarding/fields/:fieldKey` is filed as a backend ask.
 */
export const MerchantOnboardingFieldsPage = () => {
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(Permission.MERCHANT_ONBOARDING_MANAGE);

  const { data: fields, isLoading, error } = useMerchantOnboardingFields();
  const saveFields = useSetMerchantOnboardingFields();

  const [draft, setDraft] = useState<Draft>({} as Draft);

  useEffect(() => {
    if (!fields) return;
    setDraft(
      Object.fromEntries(fields.map((field) => [field.fieldKey, field.requirement])) as Draft,
    );
  }, [fields]);

  const pending = useMemo(
    () => (fields ? buildOnboardingFieldsPayload(fields, draft).fields : []),
    [fields, draft],
  );

  const save = () => {
    if (!fields || pending.length === 0) return;
    saveFields.mutate({ fields: pending });
  };

  const Heading = (
    <div className="mb-5">
      <h1 className="text-[22px] font-semibold text-foreground">Sign-up Fields</h1>
      <p className="text-[13px] text-muted-foreground mt-1">
        Which details a merchant must provide to self-onboard. Changes apply to the very next
        sign-up — no deploy needed.
      </p>
    </div>
  );

  if (isLoading) {
    return (
      <div className="py-6 max-w-4xl mx-auto px-2">
        {Heading}
        <div className="bg-card rounded-2xl border border-border p-6 text-[14px] text-muted-foreground">
          Loading sign-up fields…
        </div>
      </div>
    );
  }

  if (error || !fields) {
    return (
      <div className="py-6 max-w-4xl mx-auto px-2">
        {Heading}
        <div className="bg-card rounded-2xl border border-border p-6 text-[14px] text-muted-foreground">
          Could not load the sign-up field configuration.
        </div>
      </div>
    );
  }

  return (
    <div className="py-6 max-w-4xl mx-auto px-2 space-y-5">
      {Heading}

      <div className="bg-card rounded-2xl border border-border overflow-hidden">
        <div className="divide-y divide-border">
          {fields.map((field) => {
            const alwaysRequired = ALWAYS_REQUIRED_FIELD_KEYS.includes(field.fieldKey);
            const value = draft[field.fieldKey] ?? field.requirement;
            const isRequired = value === REQUIRED;
            const changed = value !== field.requirement;

            return (
              <div
                key={field.fieldKey}
                className="flex flex-wrap items-start justify-between gap-4 p-5"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[14px] font-semibold text-foreground">
                      {FIELD_LABELS[field.fieldKey]}
                    </span>
                    {/* Truthful badge: flips to "Admin override" only once a row exists
                        server-side, which is why Save sends changed keys only. */}
                    <span
                      className={`text-[12px] px-2 py-0.5 rounded-full ${
                        field.isDefault
                          ? 'bg-muted text-muted-foreground'
                          : 'bg-blue-100 text-blue-700'
                      }`}
                    >
                      {field.isDefault ? 'Platform default' : 'Admin override'}
                    </span>
                    {changed && (
                      <span className="text-[12px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                        Unsaved
                      </span>
                    )}
                  </div>
                  <p className="text-[12px] text-muted-foreground mt-0.5">
                    {FIELD_DESCRIPTIONS[field.fieldKey]}
                  </p>
                  {!field.isDefault && field.updatedAt && (
                    <p className="text-[12px] text-muted-foreground mt-0.5">
                      Overridden {formatDate(field.updatedAt)}
                    </p>
                  )}
                  {alwaysRequired && (
                    <p className="text-[12px] text-muted-foreground mt-0.5">
                      {ALWAYS_REQUIRED_REASON}
                    </p>
                  )}
                </div>

                {/* The Optional option is DISABLED for the three fields watchman rejects with
                    a 400 — the toggle can only ever be moved toward Required for them. */}
                <div className="shrink-0">
                  <Toggle
                    label={isRequired ? 'Required' : 'Optional'}
                    checked={isRequired}
                    disabled={!canManage || alwaysRequired}
                    title={
                      alwaysRequired
                        ? ALWAYS_REQUIRED_REASON
                        : canManage
                          ? undefined
                          : 'You do not have permission to change sign-up fields.'
                    }
                    onChange={(checked) =>
                      setDraft((prev) => ({
                        ...prev,
                        [field.fieldKey]: checked ? REQUIRED : OPTIONAL,
                      }))
                    }
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {canManage && (
        <div className="flex flex-wrap items-center justify-end gap-3">
          <span className="text-[12px] text-muted-foreground">
            {pending.length === 0
              ? 'No unsaved changes.'
              : `${pending.length} field${pending.length === 1 ? '' : 's'} changed.`}
          </span>
          <Button
            size="sm"
            className="h-[40px]"
            onClick={save}
            disabled={pending.length === 0 || saveFields.isPending}
            loading={saveFields.isPending}
          >
            Save changes
          </Button>
        </div>
      )}
    </div>
  );
};
